'use client';

import React, { useState } from 'react';
import { motion, useMotionValue, useTransform, AnimatePresence, PanInfo } from 'framer-motion';
import Image from 'next/image';
import Icon from '@/components/ui/AppIcon';

// Reusing the Project Interface and Data locally for now to avoid prop-drilling hell.
// Ideally, this should be in a shared constants file, but for this component's self-containment we'll mirror it.
// The types match ProjectCarousel3D.tsx.

interface Project {
    id: number;
    title: string;
    category: string;
    desc: string;
    color: string;
    tags: string[];
    link: string;
    image?: string;
    imageFit?: "cover" | "contain" | "fill";
}

const PROJECTS: Project[] = [
    {
        id: 1,
        title: "Ak47 Sports",
        category: "E-Commerce",
        desc: "High-performance sports accessories platform.",
        color: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)", // Deep Blue
        tags: ["Next.js", "Shopify", "Tailwind"],
        link: "https://ak47sports.com",
        image: "/projects/ak47-card-bg.png",
        imageFit: "cover"
    },
    {
        id: 2,
        title: "Pincode Hotels",
        category: "Hospitality",
        desc: "Hotel booking & convention hall event management.",
        color: "linear-gradient(135deg, #4c1d95 0%, #8b5cf6 100%)", // Violet
        tags: ["Booking Engine", "Calendar", "React"],
        link: "https://pincodehotels.com",
        image: "/projects/pincode-card-bg.png",
        imageFit: "cover"
    },
    {
        id: 3,
        title: "Point of Sale",
        category: "Retail System",
        desc: "In-store POS for seamless retail operations.",
        color: "linear-gradient(135deg, #0f172a 0%, #334155 100%)", // Slate Tech
        tags: ["Real-time", "Inventory", "Offline-first"],
        link: "https://pos.ak47sports.com/pos",
        image: "/projects/pos-card-bg.png",
        imageFit: "contain"
    },
    {
        id: 4,
        title: "Inventory Mgmt",
        category: "SaaS Product",
        desc: "Stock adjustment, barcode printing & logistics.",
        color: "linear-gradient(135deg, #047857 0%, #10b981 100%)", // Emerald
        tags: ["Barcodes", "Dashboard", "Analytics"],
        link: "https://pos.ak47sports.com/inventory",
        image: "/projects/inventory-card-bg.png",
        imageFit: "contain"
    },
    {
        id: 5,
        title: "Admin System",
        category: "Internal Tool",
        desc: "Centralized CMS for image & text content management.",
        color: "linear-gradient(135deg, #be123c 0%, #f43f5e 100%)", // Rose
        tags: ["CMS", "RBAC", "Media Library"],
        link: "https://pos.ak47sports.com/services",
        image: "/projects/admin-card-bg.png",
        imageFit: "contain"
    }
];

const MobileLiveProjectsShowcase = () => {
    // Managing the stack of cards
    // 0 is the front-most card.
    const [cards, setCards] = useState(PROJECTS);

    const removeCard = (id: number) => {
        setCards((current) => {
            const newCards = [...current];
            // Move the first card to the end (circular queue)
            const movedCard = newCards.shift();
            if (movedCard) newCards.push(movedCard);
            return newCards;
        });
    };

    return (
        <div className="w-full min-h-[450px] flex flex-col items-center justify-center relative overflow-hidden py-6">
            {/* Header Area */}
            <div className="text-center mb-6 relative z-10 px-6">
                <span className="text-xs font-bold tracking-[0.2em] text-slate-400 uppercase mb-3 flex items-center justify-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse box-shadow-green" style={{ boxShadow: '0 0 10px #22c55e' }}></span>
                    Live Deployments
                </span>
                <h2 className="text-4xl font-black text-slate-900 leading-tight">
                    Ecosystems <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-500 italic font-light">in</span> Action.
                </h2>
                <p className="text-slate-500 text-sm mt-2 font-medium">
                    Swipe to explore our work.
                </p>
            </div>

            {/* The Swipe Deck */}
            <div className="relative w-full max-w-sm h-[400px] flex items-center justify-center">
                {/* We map REVERSE so the first element (index 0) renders LAST (on top) */}
                {cards.map((project, index) => {
                    const isFront = index === 0;
                    return (
                        <SwipeCard
                            key={project.id}
                            project={project}
                            index={index}
                            isFront={isFront}
                            onRemove={() => removeCard(project.id)}
                        />
                    );
                }).reverse()}
            </div>
        </div>
    );
};

// --- Individual Swipeable Card --- 

interface SwipeCardProps {
    project: Project;
    index: number;
    isFront: boolean;
    onRemove: () => void;
}

const SwipeCard = ({ project, index, isFront, onRemove }: SwipeCardProps) => {
    // Motion Values for the draggable card
    const x = useMotionValue(0);
    const rotate = useTransform(x, [-200, 200], [-15, 15]); // Tilt while dragging
    const opacity = useTransform(x, [-200, -100, 0, 100, 200], [0, 1, 1, 1, 0]); // Fade out on exit

    // Stack Styles calculation
    // Front card (index 0): Scale 1, Y 0
    // Second card (index 1): Scale 0.95, Y 10
    // Third card (index 2): Scale 0.9, Y 20
    // Others hidden
    const isVisible = index <= 2;
    const scale = 1 - index * 0.05;
    const yOffset = index * 15;
    const zIndex = 100 - index;
    const backgroundOpacity = 1 - index * 0.2; // Dim cards in back

    function handleDragEnd(_: any, info: PanInfo) {
        const threshold = 50; // Reduced threshold for easier swipes
        const velocity = Math.abs(info.velocity.x);

        if (info.offset.x > threshold || (velocity > 500 && info.offset.x > 0)) {
            // Swiped Right
            onRemove();
        } else if (info.offset.x < -threshold || (velocity > 500 && info.offset.x < 0)) {
            // Swiped Left
            onRemove();
        }
    }

    if (!isVisible) return null;

    return (
        <motion.div
            style={{
                x: isFront ? x : 0, // Only front card moves
                rotate: isFront ? rotate : 0, // Only front card rotates
                zIndex,
                scale,
                y: yOffset,
                opacity: isFront ? opacity : backgroundOpacity,
            }}
            drag={isFront ? "x" : false} // Only front card is draggable
            dragConstraints={{ left: 0, right: 0 }} // Snap back if released early
            onDragEnd={handleDragEnd}
            animate={{
                scale,
                y: yOffset,
                opacity: isFront ? 1 : backgroundOpacity,
            }}
            transition={{
                type: "spring",
                stiffness: 260,
                damping: 20,
            }}
            className={`absolute w-[300px] h-[380px] bg-white rounded-3xl shadow-2xl overflow-hidden cursor-grab active:cursor-grabbing border border-slate-100/50 ${isFront ? 'touch-none' : ''}`}
        >
            {/* --- CARD CONTENT --- */}

            {/* Visual Top Half */}
            <div className="h-[52%] w-full relative p-6 flex flex-col justify-end overflow-hidden"
                style={{ background: project.color }}>

                {/* Image */}
                {project.image && (
                    <Image
                        src={project.image}
                        alt={project.title}
                        fill
                        className={`object-cover ${project.imageFit === 'contain' ? 'object-contain p-4' : ''}`}
                        draggable={false}
                    />
                )}

                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent pointer-events-none" />

                <div className="relative z-10 text-white">
                    <span className="inline-block px-2 py-1 rounded bg-white/20 backdrop-blur-md text-[10px] font-bold uppercase tracking-wider mb-2 border border-white/10">
                        {project.category}
                    </span>
                    <h3 className="text-2xl font-bold leading-none">{project.title}</h3>
                </div>
            </div>

            {/* Content Bottom Half */}
            <div className="h-[48%] p-5 flex flex-col justify-between bg-white relative">
                <p className="text-slate-600 text-sm leading-relaxed line-clamp-3">
                    {project.desc}
                </p>

                <div className="flex flex-wrap gap-2 mt-2">
                    {project.tags.slice(0, 3).map(tag => (
                        <span key={tag} className="text-[10px] px-2 py-1 rounded-md bg-slate-100 text-slate-500 font-bold">
                            {tag}
                        </span>
                    ))}
                </div>

                {/* Visit Button */}
                <a
                    href={project.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 flex items-center justify-between group pt-4 border-t border-slate-100"
                    onPointerDown={(e) => e.stopPropagation()} // Prevent drag when trying to click
                >
                    <span className="text-xs font-bold text-slate-900 group-hover:text-blue-600 transition-colors">
                        Visit Live Site
                    </span>
                    <div className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-all shadow-sm">
                        <Icon name="ArrowRightIcon" className="w-3 h-3" />
                    </div>
                </a>
            </div>

        </motion.div>
    );
};

export default MobileLiveProjectsShowcase;
